import React, { useState, useEffect, useRef, useCallback } from 'react';
import CompanyService from '../../services/CompanyService';
import { DEFAULT_LOGO_URL } from '../../utils/imageUrlHelper';

let initialCompaniesRequest = null;

const CompanySelector = () => {
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const lastNotifiedCompanyIdRef = useRef(null);
  const selectedCompanyRef = useRef(null);

  useEffect(() => {
    selectedCompanyRef.current = selectedCompany;
  }, [selectedCompany]);

  useEffect(() => {
    fetchCompanies();

    // Escuchar evento de recarga de empresas
    const handleCompaniesUpdated = () => {
      fetchCompanies();
    };
    window.addEventListener('companiesUpdated', handleCompaniesUpdated);

    return () => {
      window.removeEventListener('companiesUpdated', handleCompaniesUpdated);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resolveNextSelectedCompany = useCallback((companiesList) => {
    if (companiesList.length === 0) {
      return null;
    }

    const savedCompanyId = localStorage.getItem('selectedCompanyId');
    if (savedCompanyId) {
      const savedCompany = companiesList.find(c => c.id === parseInt(savedCompanyId, 10));
      if (savedCompany) {
        return savedCompany;
      }
    }

    if (selectedCompanyRef.current) {
      const currentCompany = companiesList.find(c => c.id === selectedCompanyRef.current.id);
      if (currentCompany) {
        return currentCompany;
      }
    }

    return companiesList[0];
  }, []);

  const syncCompanySelection = useCallback((company, options = {}) => {
    const { forceNotify = false } = options;
    const previousCompanyId = selectedCompanyRef.current?.id ?? null;
    const nextCompanyId = company?.id ?? null;
    const hasCompanyChanged = previousCompanyId !== nextCompanyId;

    setSelectedCompany(company);

    if (!company) {
      localStorage.removeItem('selectedCompanyId');
      if (forceNotify || lastNotifiedCompanyIdRef.current !== null) {
        window.dispatchEvent(new CustomEvent('companyChanged', {
          detail: { companyId: null, company: null }
        }));
        lastNotifiedCompanyIdRef.current = null;
      }
      return;
    }

    localStorage.setItem('selectedCompanyId', company.id);
    applyCompanyTheme(company);

    if (forceNotify || hasCompanyChanged || lastNotifiedCompanyIdRef.current !== company.id) {
      window.dispatchEvent(new CustomEvent('companyChanged', {
        detail: {
          companyId: company.id,
          company
        }
      }));
      lastNotifiedCompanyIdRef.current = company.id;
    }
  }, []);

  const fetchCompanies = async () => {
    try {
      if (!initialCompaniesRequest) {
        initialCompaniesRequest = CompanyService.getAll(0, 100, { name: '', enabled: true });
      }

      const response = await initialCompaniesRequest;
      const companiesList = response.content || [];
      setCompanies(companiesList);

      if (companiesList.length === 0) {
        syncCompanySelection(null, { forceNotify: true });
        return;
      }

      const nextCompany = resolveNextSelectedCompany(companiesList);
      syncCompanySelection(nextCompany);
    } catch (error) {
      console.error('Error loading companies:', error);
    } finally {
      initialCompaniesRequest = null;
    }
  };

  const handleCompanyChange = (company) => {
    syncCompanySelection(company, { forceNotify: true });
    setIsOpen(false);
  };

  const applyCompanyTheme = (company) => {
    const root = document.documentElement;
    
    // Aplicar estilos directamente
    const primaryColor = company.primaryColor || '#2563EB';
    const fontPrimaryColor = company.fontPrimaryColor || '#ffffff';
    const headerBg = company.headerFooterColor || '#ffffff';
    const headerFont = company.fontHeaderFooterColor || '#000000';
    
    root.style.setProperty('--primary-color', primaryColor);
    root.style.setProperty('--primary-font-color', fontPrimaryColor);
    root.style.setProperty('--header-footer-bg', headerBg);
    root.style.setProperty('--header-footer-font', headerFont);

    // Disparar evento para actualizar tema
    const event = new CustomEvent('themeUpdated', { 
      detail: { 
        primaryColor,
        fontPrimaryColor,
        headerFooterColor: headerBg,
        fontHeaderFooterColor: headerFont
      } 
    });
    window.dispatchEvent(event);

    // Disparar evento para actualizar logo
    const isSameCompany = selectedCompanyRef.current?.id === company.id;
    const logoUrl = company.logoImage || (isSameCompany ? null : DEFAULT_LOGO_URL);
    if (logoUrl) {
      const logoEvent = new CustomEvent('logoUpdated', { detail: { logoUrl } });
      window.dispatchEvent(logoEvent);
    }

  };

  if (companies.length === 0) {
    return null;
  }

  return (
    <div className="company-selector-dropdown">
      <button 
        className="company-toggle"
        onClick={() => setIsOpen(!isOpen)}
        title={selectedCompany?.name || ''}
      >
        <span className="company-name">{selectedCompany?.name || 'Empresa'}</span>
        <i className={`fa fa-chevron-down company-chevron ${isOpen ? 'open' : ''}`}></i>
      </button>

      {isOpen && (
        <>
          <div className="company-overlay" onClick={() => setIsOpen(false)}></div>
          <div className="company-menu">
            {companies.map((company) => (
              <button
                key={company.id}
                className={`company-item ${selectedCompany?.id === company.id ? 'active' : ''}`}
                onClick={() => handleCompanyChange(company)}
              >
                <i className="fa fa-building"></i>
                <span className="company-full-name">{company.name}</span>
                {selectedCompany?.id === company.id && (
                  <i className="fa fa-check company-check"></i>
                )}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default CompanySelector;

